import React from 'react';
import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { KpiCard } from '../../../components/app';
import { TreasurySummaryProps } from '../types';

const fmt = (n: number) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(n);
const day = (d: string) => d.slice(0, 10).split('-').reverse().join('.');

/**
 * Итог одним рядом: Касса (hero), Долг перед основателями, Прибыль к распределению и
 * сходится ли Касса с Фактическим остатком. Под ним — из чего сложилась Касса.
 */
export function TreasurySummary({ overview, isLoading, formatMoney }: TreasurySummaryProps) {
  const loading = isLoading || !overview;
  const cash = overview?.cash;
  const totalDebt = overview?.totalDebt ?? 0;
  const bankCheck = overview?.bankCheck ?? null;

  const parts: Array<{ label: string; value: number; sign: '+' | '−' }> = cash
    ? [
        { label: 'поступило', value: cash.settled, sign: '+' },
        { label: 'взносы', value: cash.contributions, sign: '+' },
        { label: 'расходы со счёта', value: cash.companyExpenses, sign: '−' },
        { label: 'возвраты', value: cash.reimbursements, sign: '−' },
        { label: 'доли прибыли', value: cash.profitPayouts, sign: '−' },
      ]
    : [];

  return (
    <section aria-label="Казна" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          variant="hero"
          label="Касса"
          value={fmt(cash?.total ?? 0)}
          unit="сум"
          hint="Деньги на Счёте компании прямо сейчас"
          loading={loading}
        />
        <KpiCard
          label="Долг перед основателями"
          value={fmt(totalDebt)}
          unit="сум"
          hint="Вложено и ещё не возвращено"
          loading={loading}
        />
        <KpiCard
          label="Прибыль к распределению"
          value={fmt(overview?.distributableProfit ?? 0)}
          unit="сум"
          hint={totalDebt > 0 ? 'Ноль, пока Вклады не возвращены' : 'Касса за вычетом долгов'}
          loading={loading}
        />
        <KpiCard
          label="Сверка с выпиской"
          value={bankCheck ? fmt(bankCheck.discrepancy) : '—'}
          unit={bankCheck ? 'сум' : undefined}
          delta={
            bankCheck
              ? bankCheck.discrepancy === 0
                ? { text: 'Сходится', tone: 'pos' }
                : { text: 'Расхождение — забыт Расход или Движение', tone: 'neg' }
              : undefined
          }
          hint={
            bankCheck
              ? `Фактический остаток на ${day(bankCheck.date)}: ${formatMoney(bankCheck.amount)}`
              : 'Фактический остаток ещё не вводили'
          }
          loading={loading}
        />
      </div>

      <Card className="flex flex-wrap items-center gap-x-5 gap-y-2 px-[18px] py-3 text-[13px] text-text-2">
        <span className="font-semibold text-text">Касса сложилась из:</span>
        {loading
          ? Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-4 w-32" />)
          : parts.map((p) => (
              <span key={p.label} className="whitespace-nowrap">
                <span className={p.sign === '+' ? 'font-bold text-pos' : 'font-bold text-text-3'} aria-hidden>
                  {p.sign}
                </span>{' '}
                <span className="sr-only">{p.sign === '+' ? 'плюс' : 'минус'} </span>
                {p.label} <span className="font-semibold text-text tabular-nums">{formatMoney(p.value)}</span>
              </span>
            ))}
      </Card>
    </section>
  );
}
